/**
 * The M3 stand-in career (M3 plan §1.2, R5): a `CareerPort` over the save's `StandInState`, mutated in place so it
 * is saved with the rest of the Reach. The M2 bridge replaces it with M2's career. Refusals change nothing.
 */
import { logger } from '../../core/logger'
import type { CareerPort } from '../ports'
import { PART_IDS, type PartId, type StandInState } from '../types'
import type { StandInConfig } from './config'

/** Float tolerance so, for example, 20 t of free space over 1 t parts never floors to 19 on rounding noise. */
const FIT_EPSILON = 1e-9

export function createStandInState(cfg: StandInConfig): StandInState {
  return { credits: cfg.startCredits, cargo: { hull_plating: 0, electronics: 0, drive_parts: 0 }, capacityT: cfg.capacityT, scannerMk: 1 }
}

export interface StandInPrices {
  parts: Readonly<Record<PartId, number>>
  massT: Readonly<Record<PartId, number>>
  scanners: Readonly<Record<2 | 3, number>>
}

/** Prices for the Parts and Scanner tabs. */
export function standInPrices(cfg: StandInConfig): StandInPrices {
  return {
    parts: { ...cfg.partPrices },
    massT: { ...cfg.partMassT },
    scanners: { 2: cfg.scannerPrices['2'], 3: cfg.scannerPrices['3'] },
  }
}

/** A whole, non-negative quantity (NaN and negatives are 0). */
function wholeQty(qty: number): number {
  return Number.isFinite(qty) ? Math.max(0, Math.floor(qty)) : 0
}

export function createStandInCareer(state: StandInState, cfg: StandInConfig): CareerPort {
  const usedT = (): number => {
    let t = 0
    for (let i = 0; i < PART_IDS.length; i++) t += state.cargo[PART_IDS[i]] * cfg.partMassT[PART_IDS[i]]
    return t
  }
  const freeT = (): number => Math.max(0, state.capacityT - usedT())
  return {
    credits: () => state.credits,
    earn(amount: number, reason: string): void {
      if (!(amount > 0) || !Number.isFinite(amount)) return
      state.credits += amount
      logger.debug('stand-in: earned', { amount, reason })
    },
    spend(amount: number, reason: string): boolean {
      if (!(amount >= 0) || !Number.isFinite(amount) || amount > state.credits) return false
      state.credits -= amount
      logger.debug('stand-in: spent', { amount, reason })
      return true
    },
    cargo: (part: PartId) => state.cargo[part],
    cargoFreeT: freeT,
    addCargo(part: PartId, qty: number): number {
      const fits = Math.floor(freeT() / cfg.partMassT[part] + FIT_EPSILON)
      const added = Math.min(wholeQty(qty), fits)
      state.cargo[part] += added
      return added
    },
    removeCargo(part: PartId, qty: number): number {
      const removed = Math.min(wholeQty(qty), state.cargo[part])
      state.cargo[part] -= removed
      return removed
    },
    scannerMk: () => state.scannerMk,
    setScannerMk(mk: 1 | 2 | 3): void {
      state.scannerMk = mk
    },
    onPlayerDeath(): void {
      if (!cfg.deathLosesParts) return
      for (let i = 0; i < PART_IDS.length; i++) state.cargo[PART_IDS[i]] = 0
    },
  }
}
